import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { WhatsappLogo, MessengerLogo, InstagramLogo } from "@phosphor-icons/react";

const ICON = { whatsapp: [WhatsappLogo, "text-emerald-600"], messenger: [MessengerLogo, "text-blue-600"], instagram: [InstagramLogo, "text-pink-600"] };
const fmtMin = (m) => (m == null ? "—" : m < 1 ? `${Math.round(m * 60)} s` : m < 60 ? `${m} min` : `${(m / 60).toFixed(1)} h`);

/** Leads, campaign replies and human first-response time per WhatsApp number / Messenger page / IG account. */
export function AccountReport({ dateFrom, dateTo, t }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    setRows(null);
    api.get("/analytics/by-account", { params: { from: dateFrom, to: dateTo } })
      .then(({ data }) => setRows(data.rows)).catch(() => setRows([]));
  }, [dateFrom, dateTo]);
  if (!rows) return <div className="text-sm text-muted-foreground py-6 text-center">{t("Cargando…", "Loading…")}</div>;
  if (!rows.length) return <div className="text-sm text-muted-foreground py-6 text-center" data-testid="account-report-empty">{t("No hay cuentas conectadas", "No connected accounts")}</div>;
  const th = "px-3 py-2 text-left text-[11px] uppercase tracking-wider font-semibold text-muted-foreground";
  return (
    <div className="border border-border rounded-lg bg-card overflow-x-auto mb-6" data-testid="account-report">
      <table className="w-full text-sm">
        <thead className="border-b border-border bg-secondary/40">
          <tr>
            <th className={th}>{t("Número / página", "Number / page")}</th>
            <th className={`${th} text-right`}>{t("Conversaciones", "Conversations")}</th>
            <th className={`${th} text-right`}>{t("Leads creados", "Leads created")}</th>
            <th className={`${th} text-right`}>{t("Respuestas a campañas", "Campaign replies")}</th>
            <th className={`${th} text-right`}>{t("1ª respuesta del equipo", "Team first response")}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((r) => {
            const [Icon, color] = ICON[r.channel] || ICON.whatsapp;
            return (
              <tr key={r.account_id} data-testid={`account-report-row-${r.account_id}`} className={r.active ? "" : "opacity-60"}>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <Icon size={16} weight="fill" className={color} />
                    <span className="font-medium">{r.label}</span>
                    {r.is_default && <span className="text-[10px] text-muted-foreground">({t("principal", "main")})</span>}
                    {!r.active && <span className="text-[10px] text-muted-foreground">({t("inactiva", "inactive")})</span>}
                  </div>
                  {r.display_phone_number && <div className="text-[11px] text-muted-foreground ml-6">{r.display_phone_number}</div>}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">{r.conversations}</td>
                <td className="px-3 py-2.5 text-right tabular-nums font-semibold" data-testid={`account-report-leads-${r.account_id}`}>{r.leads}</td>
                <td className="px-3 py-2.5 text-right tabular-nums" data-testid={`account-report-replies-${r.account_id}`}>
                  {r.campaign_sent ? <>{r.campaign_replied}/{r.campaign_sent} <span className="text-muted-foreground text-xs">({r.campaign_reply_pct}%)</span></> : "—"}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums" data-testid={`account-report-frt-${r.account_id}`}>
                  {fmtMin(r.frt_minutes)}
                  {r.frt_count > 0 && <span className="text-muted-foreground text-xs"> · {r.frt_count}</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="px-3 py-2 text-[10px] text-muted-foreground border-t border-border">
        {t("Respuestas a campañas: clientes que escribieron dentro de 72 h tras recibir la campaña desde ese número. 1ª respuesta: desde que la conversación pasa a un asesor hasta su primer mensaje.",
           "Campaign replies: customers who wrote back within 72 h of receiving the campaign from that number. First response: from handoff to the advisor's first message.")}
      </div>
    </div>
  );
}

export default AccountReport;
